import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import type { FrameOption } from '@renderer/types/configState'
import BkScreen from './BkScreen'
import { useBkPageTimeout } from './pageTimeout'
import BkTimer from './BkTimer'
import BkStrip from './BkStrip'
import { BK_ASSET, BK_HIT, BK_STICKER_GRID, bkHit, stripBox } from './layout'
import { categoryOfFrame, isCollabFrame, photoRatioOf } from './cutCategories'
import { useBkPhotos } from './useBkPhotos'

/**
 * Screens 6 and 7 — "select your border".
 *
 * Two modes, one per plate: the house set, and the bk x brenbaru collaboration
 * behind "more design". Each is a horizontally scrolling rail, so a long set can
 * also just be dragged.
 *
 * "More design" is shown on BOTH plates and toggles between them. The collab
 * plate was drawn without that swan, which would strand a guest who wanted to
 * go back — see BK_HIT.frame.moreDesignPatch for how it gets one.
 */
const Frame: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, uploadSessionState, logActivity } = useSessionStateStore()
  const { frameCategories, frames: allFrames, config } = useConfigStore()
  const frameId = useSessionStateStore((s) => s.userPreferences?.frameId)
  const photos = useBkPhotos()
  const railRef = useRef<HTMLDivElement>(null)
  const [mode, setMode] = useState<'house' | 'collab'>('house')

  const borders = useMemo(() => {
    const category = categoryOfFrame(frameCategories, frameId)
    return category?.frames ?? allFrames
  }, [frameCategories, frameId, allFrames])

  const shown = useMemo(
    () => borders.filter((b) => (mode === 'collab' ? isCollabFrame(b) : !isCollabFrame(b))),
    [borders, mode]
  )
  const frame = allFrames.find((f) => f._id === frameId)

  const chooseBorder = (border: FrameOption): void => {
    setSessionState({
      userPreferences: { frameId: border._id, framePhotoRatio: photoRatioOf(border) }
    })
    logActivity(`BK border chosen: ${border.name}`)
  }

  const toggleMode = (): void => {
    setMode((m) => (m === 'house' ? 'collab' : 'house'))
    railRef.current?.scrollTo({ left: 0, behavior: 'auto' })
    logActivity(`BK border set: ${mode === 'house' ? 'brenbaru' : 'house'}`)
  }

  const confirm = (): void => {
    logActivity('BK border confirmed', { to: 'edit' })
    uploadSessionState()
    navigate(`/${projectId}/edit`)
  }
  const remaining = useBkPageTimeout(config?.pages?.frame?.settings?.frameTimeout, confirm)

  // A cut with one border has nothing to choose: the 6x8 sheets ship with one
  // design per cut, so this page is passed straight through. Adding a second
  // border to the category in the Studio brings the page back.
  const singleBorder = borders.length <= 1
  const skippedRef = useRef(false)
  useEffect(() => {
    if (!singleBorder || skippedRef.current) return
    skippedRef.current = true
    logActivity('BK border page skipped: the cut has one border')
    confirm()
    // confirm is recreated every render; the ref makes this run once.
  }, [singleBorder])
  if (singleBorder) return null

  return (
    <BkScreen plate={mode === 'house' ? 'border.webp' : 'border-brenbaru.webp'}>
      <div style={bkHit(stripBox(frame?.drawParams.canvasWidth, frame?.drawParams.canvasHeight))}>
        <BkStrip frame={frame} photos={photos} className="h-full w-full" />
      </div>

      <div
        ref={railRef}
        style={bkHit(BK_STICKER_GRID)}
        /* A soft rail scrollbar: enough to say "there is more this way" without
           putting a browser chrome bar on painted artwork. */
        className="flex items-center gap-6 overflow-x-auto scroll-smooth px-2 pb-3 [scrollbar-color:rgba(240,238,220,0.55)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#F0EEDC]/55 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar]:bg-transparent"
      >
        {shown.map((border) => (
          <button
            key={border._id}
            onClick={() => chooseBorder(border)}
            aria-label={border.name}
            aria-pressed={border._id === frameId}
            className={`h-full shrink-0 rounded-sm border-[3px] transition ${
              border._id === frameId ? 'border-white' : 'border-transparent opacity-85'
            }`}
          >
            <img src={border.thumbnail} alt="" className="h-full w-auto object-contain" />
          </button>
        ))}
      </div>

      <button
        aria-label="Confirm"
        onClick={confirm}
        style={bkHit(BK_HIT.frame.confirm)}
        className="cursor-pointer bg-transparent"
      />
      {/* On the collab plate the swan is ours, lifted from the house plate. */}
      {mode === 'collab' && (
        <img
          src={BK_ASSET('more-design.webp')}
          alt=""
          aria-hidden
          style={bkHit(BK_HIT.frame.moreDesignPatch)}
          className="pointer-events-none"
        />
      )}
      <button
        aria-label={mode === 'house' ? 'More designs' : 'Back to the house set'}
        onClick={toggleMode}
        style={bkHit(BK_HIT.frame.moreDesign)}
        className="cursor-pointer bg-transparent"
      />
      <BkTimer seconds={remaining} />
    </BkScreen>
  )
}

export default Frame
